import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useCacheStore } from '../cache'
import { adminRefundsApi } from '@/https'
import type {
    AdminRefundListRequest,
    AdminRefundOverviewStats,
    RefundListItem,
    AdminApproveRefundRequest,
    AdminRejectRefundRequest,
} from '@/https/types_admin/refunds'

export const useAdminRefundsStore = defineStore('admin-refunds', () => {
    const cacheStore = useCacheStore()

    // 列表与详情
    const refundList = ref<RefundListItem[]>([])
    const currentRefund = ref<RefundListItem | null>(null)

    // 分页
    const pagination = ref({ total: 0, page: 1, size: 20, has_more: false })
    const lastQuery = ref<AdminRefundListRequest>({})
    const overviewStats = ref<AdminRefundOverviewStats>({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        success: 0,
        failed: 0,
    })

    // 加载与错误
    const listLoading = ref(false)
    const detailLoading = ref(false)
    const operateLoading = ref(false)
    const listError = ref<string | null>(null)
    const detailError = ref<string | null>(null)
    const operateError = ref<string | null>(null)

    // 加载列表
    const loadRefundList = async (params?: AdminRefundListRequest, forceRefresh = false) => {
        const query = { ...lastQuery.value, ...params }
        lastQuery.value = query
        try {
            listLoading.value = true
            listError.value = null
            const resp = await cacheStore.smartCache(
                `admin-refunds:list:${JSON.stringify(query)}`,
                () => adminRefundsApi.getRefundList(query),
                forceRefresh
            )
            refundList.value = resp.items
            pagination.value = { total: resp.total, page: resp.page, size: resp.size, has_more: resp.has_more }
        } catch (err) {
            listError.value = err instanceof Error ? err.message : '加载退款列表失败'
            console.error('加载退款列表失败:', err)
        } finally {
            listLoading.value = false
        }
    }

    const loadRefundOverview = async (params?: AdminRefundListRequest, forceRefresh = false) => {
        const query = { ...lastQuery.value, ...params }
        lastQuery.value = query
        try {
            listLoading.value = true
            listError.value = null
            const resp = await cacheStore.smartCache(
                `admin-refunds:overview:${JSON.stringify(query)}`,
                () => adminRefundsApi.getRefundOverview(query),
                forceRefresh
            )
            refundList.value = resp.list.items
            pagination.value = {
                total: resp.list.total,
                page: resp.list.page,
                size: resp.list.size,
                has_more: resp.list.has_more,
            }
            overviewStats.value = resp.stats
        } catch (err) {
            listError.value = err instanceof Error ? err.message : '加载退款概览失败'
            console.error('加载退款概览失败:', err)
        } finally {
            listLoading.value = false
        }
    }

    // 加载详情
    const loadRefundDetail = async (id: number, forceRefresh = false) => {
        try {
            detailLoading.value = true
            detailError.value = null
            const resp = await cacheStore.smartCache(
                `admin-refunds:detail:${id}`,
                () => adminRefundsApi.getRefundDetail(id),
                forceRefresh
            )
            currentRefund.value = resp
        } catch (err) {
            detailError.value = err instanceof Error ? err.message : '加载退款详情失败'
            console.error(`加载退款详情失败 (ID: ${id}):`, err)
        } finally {
            detailLoading.value = false
        }
    }

    // 管理员代客申请退款（在订单详情入口使用）
    const createRefund = async (order_id: number, reason?: string) => {
        try {
            operateLoading.value = true
            operateError.value = null
            await adminRefundsApi.createRefund({ order_id, reason })
            cacheStore.removeCacheByPrefix('admin-refunds:list')
            cacheStore.removeCacheByPrefix('admin-refunds:overview')
        } catch (err) {
            operateError.value = err instanceof Error ? err.message : '创建退款申请失败'
            console.error('创建退款申请失败:', err)
            throw err
        } finally {
            operateLoading.value = false
        }
    }

    // 审批通过
    const approveRefund = async (id: number, data?: AdminApproveRefundRequest) => {
        try {
            operateLoading.value = true
            operateError.value = null
            await adminRefundsApi.approveRefund(id, data)
            cacheStore.removeCacheByPrefix('admin-refunds:list')
            cacheStore.removeCacheByPrefix('admin-refunds:overview')
            cacheStore.removeCacheByPrefix(`admin-refunds:detail:${id}`)
            await loadRefundDetail(id, true)
        } catch (err) {
            operateError.value = err instanceof Error ? err.message : '审批通过失败'
            console.error(`审批通过失败 (ID: ${id}):`, err)
            throw err
        } finally {
            operateLoading.value = false
        }
    }

    // 审批驳回
    const rejectRefund = async (id: number, data?: AdminRejectRefundRequest) => {
        try {
            operateLoading.value = true
            operateError.value = null
            await adminRefundsApi.rejectRefund(id, data)
            cacheStore.removeCacheByPrefix('admin-refunds:list')
            cacheStore.removeCacheByPrefix('admin-refunds:overview')
            cacheStore.removeCacheByPrefix(`admin-refunds:detail:${id}`)
            await loadRefundDetail(id, true)
        } catch (err) {
            operateError.value = err instanceof Error ? err.message : '审批驳回失败'
            console.error(`审批驳回失败 (ID: ${id}):`, err)
            throw err
        } finally {
            operateLoading.value = false
        }
    }

    const clearAllStates = () => {
        refundList.value = []
        currentRefund.value = null
        pagination.value = { total: 0, page: 1, size: 20, has_more: false }
        lastQuery.value = {}
        overviewStats.value = { total: 0, pending: 0, approved: 0, rejected: 0, success: 0, failed: 0 }
        listError.value = null
        detailError.value = null
        operateError.value = null
    }

    return {
        // 状态
        refundList,
        currentRefund,
        pagination,
        lastQuery,
        overviewStats,
        listLoading,
        detailLoading,
        operateLoading,
        listError,
        detailError,
        operateError,
        
        // 方法
        loadRefundList,
        loadRefundOverview,
        loadRefundDetail,
        createRefund,
        approveRefund,
        rejectRefund,
        clearAllStates,
    }
})
